import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { tokenSelector } from 'ducks/user'
import {
  INACTIVITY_TIMEOUT_MS,
  SESSION_LOCK_EVENT_KEY,
} from 'constants/global.constants'

const activityEvents: string[] = [
  'click',
  'keydown',
  'mousedown',
  'pointerdown',
  'scroll',
  'touchstart',
  'input',
  'submit',
]

interface SessionTimeoutState {
  remainingSeconds: number
  isLocked: boolean
  unlockSession: () => void
}

const useSessionTimeout = (): SessionTimeoutState => {
  const token = useSelector(tokenSelector)
  const timeoutRef = useRef<number | null>(null)
  const intervalRef = useRef<number | null>(null)
  const deadlineRef = useRef<number>(0)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    Math.ceil(INACTIVITY_TIMEOUT_MS / 1000),
  )

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const lockSession = useCallback(
    (broadcast: boolean): void => {
      if (isLocked) {
        return
      }

      if (broadcast) {
        localStorage.setItem(SESSION_LOCK_EVENT_KEY, String(Date.now()))
      }

      clearTimer()
      setIsLocked(true)
    },
    [clearTimer, isLocked],
  )

  const resetTimer = useCallback((): void => {
    clearTimer()

    if (!token) {
      setRemainingSeconds(Math.ceil(INACTIVITY_TIMEOUT_MS / 1000))
      return
    }

    deadlineRef.current = Date.now() + INACTIVITY_TIMEOUT_MS
    setRemainingSeconds(Math.ceil(INACTIVITY_TIMEOUT_MS / 1000))

    intervalRef.current = window.setInterval(() => {
      const secondsUntilTimeout: number = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000),
      )
      setRemainingSeconds(secondsUntilTimeout)
    }, 1000)

    timeoutRef.current = window.setTimeout(() => {
      lockSession(true)
    }, INACTIVITY_TIMEOUT_MS)
  }, [clearTimer, lockSession, token])

  const unlockSession = useCallback((): void => {
    setIsLocked(false)
  }, [])

  useEffect(() => {
    if (!token) {
      clearTimer()
      return
    }

    const handleUserActivity = (): void => {
      if (!isLocked) {
        resetTimer()
      }
    }

    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === SESSION_LOCK_EVENT_KEY && event.newValue) {
        lockSession(false)
      }
    }

    if (!isLocked) {
      resetTimer()
    }

    activityEvents.forEach((eventName: string) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true })
    })
    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearTimer()
      activityEvents.forEach((eventName: string) => {
        window.removeEventListener(eventName, handleUserActivity)
      })
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [clearTimer, isLocked, lockSession, resetTimer, token])

  return { remainingSeconds, isLocked, unlockSession }
}

export default useSessionTimeout
